import api from '../../utils/api';
import history from '../../utils/history';
import { formatMany } from '../../utils/formatHelper';
import { getRetailerStores } from './api/retailers';
import { alertError, alertSuccess } from './alert';
import {
  GET_STORES,
  UPDATE_CART,
  ADD_ID_RETAILER,
  CART_LOADING,
  CART_LOADING_FORM,
  SUCCESS_ORDER,
  CART_ERROR,
  GET_CART_VARIANTS,
  UPDATE_CART_VARIANTS,
} from '../constants/cartTypes';

const setCartInfoLoading = (value) => ({ type: CART_LOADING, payload: value });
const setCartLoadingForm = (value) => ({ type: CART_LOADING_FORM, payload: value });
const cartAddRetailerId = (value) => ({ type: ADD_ID_RETAILER, payload: value });
const cartGetCartVariants = (variants) => ({ type: GET_CART_VARIANTS, payload: variants });
const cartUpdateCartVariants = (variantId, quantity) => ({
  type: UPDATE_CART_VARIANTS,
  payload: { variantId, quantity },
});
const getStores = (stores) => ({ type: GET_STORES, payload: stores });
const successOrder = (value) => ({ type: SUCCESS_ORDER, payload: value });
const cartError = (value) => ({ type: CART_ERROR, payload: value });

export const updateCart = (variantQuantities) => (dispatch, getState) => {
  try {
    const state = getState();
    const { cartsData } = state.cart;
    const {
      retailerId,
      user: { id: userId },
    } = state.role;

    let retailerIdCart = cartsData.find((item) => item.retailer_id === retailerId && item.user_id === userId);
    if (!retailerIdCart) {
      dispatch(cartAddRetailerId({ retailerId, userId }));

      retailerIdCart = {
        user_id: userId,
        retailer_id: retailerId,
        variants: {},
      };
    }

    let itemsUpdatedCount = 0;
    Object.keys(variantQuantities).forEach((variantId) => {
      if (variantQuantities[variantId] === 0) {
        itemsUpdatedCount -= retailerIdCart.variants[variantId] || 0;
        delete retailerIdCart.variants[variantId];
      } else {
        itemsUpdatedCount += variantQuantities[variantId] - (retailerIdCart.variants[variantId] || 0);
        retailerIdCart.variants[variantId] = variantQuantities[variantId];
      }
    });

    dispatch({ type: UPDATE_CART, payload: retailerIdCart });

    if (itemsUpdatedCount !== 0) {
      let message;
      if (itemsUpdatedCount > 0) {
        message = `${itemsUpdatedCount} ${formatMany(itemsUpdatedCount, 'item')} added to the cart.`;
      } else {
        message = `${-itemsUpdatedCount} ${formatMany(-itemsUpdatedCount, 'item')} removed from the cart.`;
      }
      dispatch(alertSuccess(message));
    }
  } catch (error) {
    dispatch(alertError(error));
  }
};

export const deleteOrderLine = (variantId) => (dispatch, getState) => {
  const state = getState();
  const { cartsData, variants } = state.cart;
  const {
    retailerId,
    user: { id: userId },
  } = state.role;

  let retailerCart = cartsData.find((item) => item.retailer_id === retailerId && item.user_id === userId);
  delete retailerCart.variants[variantId];

  dispatch({ type: UPDATE_CART, payload: retailerCart });
  dispatch(cartGetCartVariants(variants.filter((variant) => variant.id !== variantId)));

  if (Object.keys(retailerCart.variants).length === 0) {
    history.push(`/retailers/${retailerId}/reorder`);
  }
};

export const setQuantityItem = (value, variantId) => (dispatch, getState) => {
  const state = getState();
  const { cartsData } = state.cart;
  const {
    retailerId,
    user: { id: userId },
  } = state.role;

  const retailerCart = cartsData.find((item) => item.retailer_id === retailerId && item.user_id === userId);
  retailerCart.variants[variantId] = value;

  dispatch({ type: UPDATE_CART, payload: retailerCart });
  dispatch(cartUpdateCartVariants(variantId, value));
};

export const getCartStores = () => async (dispatch, getState) => {
  dispatch(setCartLoadingForm(true));
  const { retailerId } = getState().role;

  try {
    const stores = await getRetailerStores(retailerId);
    dispatch(getStores(stores.data));
  } catch (error) {
    dispatch(alertError(error));
  } finally {
    dispatch(setCartLoadingForm(false));
  }
};

export const getCartVariants = () => async (dispatch, getState) => {
  dispatch(setCartInfoLoading(true));

  const state = getState();
  const { cartsData } = state.cart;
  const {
    retailerId,
    user: { id: userId },
  } = state.role;

  try {
    const retailerCart = cartsData.find(
      (retailerCart) => retailerCart.retailer_id === retailerId && retailerCart.user_id === userId,
    );
    if (retailerCart) {
      const variantIds = Object.keys(retailerCart.variants).map((id) => +id);
      if (!variantIds.length) {
        return history.push(`/retailers/${retailerId}/reorder`);
      }

      const variants = await getVariantsData(retailerId, variantIds);
      variants.data.forEach((variant) => {
        variant.quantity = +retailerCart.variants[variant.id];
      });
      dispatch(cartGetCartVariants(variants.data));
    } else {
      dispatch(cartGetCartVariants([]));
    }
  } catch (error) {
    dispatch(alertError(error));
  } finally {
    dispatch(setCartInfoLoading(false));
  }
};

const cartErrorMessage = (code, message) => {
  switch (code) {
    case 'NON_EXISTING_VARIANT_IDS':
      return `Couldn't find products`;

    case 'NO_INVENTORY_FOR_VARIANT_ID':
      return `Inventory is insufficient for some of the products and need to be adjusted - please go over the edited quantities and try placing the order again`;

    case 'SHIPPING_METHOD_CODE':
    case 'NON_EXISTING_SKUS':
    case 'NO_INVENTORY_FOR_SKUS':
    default:
      return message;
  }
};

export const sendOrder =
  (storeId, orderLines, shippingAddress, shippingMethodCode, poNumber = null) =>
  (dispatch, getState) => {
    dispatch(setCartLoadingForm(true));

    const {
      retailerId,
      user: { id: userId },
    } = getState().role;

    const payload = {
      shipping_address: shippingAddress,
      shipping_method_code: shippingMethodCode,
      order_lines: orderLines,
    };

    if (poNumber) {
      payload.po_number = poNumber;
    }

    api
      .post(`/retailers/${retailerId}/stores/${storeId}/orders`, payload)
      .then((response) => {
        history.push(`/retailers/${retailerId}/reorder`);
        dispatch(alertSuccess('Order was sent'));
        dispatch(successOrder({ retailerId, userId }));
        return response;
      })
      .catch((error) => {
        const data = error.response.data;
        if (data) {
          const message = data.code ? cartErrorMessage(data.code, data.message) : data.message;
          dispatch(cartError(message));
          dispatch(getCartVariants());
        } else {
          dispatch(alertError(error));
        }
      })
      .finally(() => {
        dispatch(setCartLoadingForm(false));
      });
  };

let cancelGetVariants;
const getVariantsData = (retailerId, variantIds) => {
  if (cancelGetVariants) {
    cancelGetVariants('Cancel get variants');
  }

  return api.get(
    `/retailers/${retailerId}/variants?service_type=replenishment&variant_ids=${JSON.stringify(variantIds)}`,
    {
      cancelToken: new api.CancelToken((c) => (cancelGetVariants = c)),
    },
  );
};
